import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Badge, Modal, Alert } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/api/roles.php');
      console.log('Roles loaded from API:', res.data);
      if (res.data && Array.isArray(res.data)) {
        setRoles(res.data);
      } else {
        console.error('Invalid roles data:', res.data);
        setError('Failed to load roles from server');
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles: ' + (err.response?.data?.error || err.message));
      // Fallback to default roles if API fails
      setRoles([
        { id: 1, name: 'admin', isSystem: 1 },
        { id: 2, name: 'doctor', isSystem: 1 },
        { id: 3, name: 'receptionist', isSystem: 1 }
      ]);
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newRole.trim()) {
      setError('Role name is required!');
      return;
    }

    // Check if role already exists
    if (roles.some(r => r.name.toLowerCase() === newRole.trim().toLowerCase())) {
      setError('Role already exists!');
      return;
    }

    try {
      console.log('Creating role:', newRole.trim());
      const res = await axios.post('/api/roles.php', { name: newRole.trim() });
      console.log('Role created:', res.data);
      
      // Refresh roles from server to ensure sync
      await fetchRoles();
      
      setNewRole('');
      setShowModal(false);
      setSuccess('Role created successfully!');
    } catch (err) {
      console.error('Error creating role:', err);
      setError(err.response?.data?.error || 'Failed to create role: ' + err.message);
    }
  };

  const handleDeleteRole = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete role "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/roles.php?id=${id}`);
      setRoles(roles.filter(r => r.id !== id));
      setSuccess('Role deleted successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
  };

  const isDefaultRole = (role) => {
    return role.isSystem === 1 || ['admin', 'doctor', 'receptionist'].includes(role.name.toLowerCase());
  };

  return (
    <Container className="my-4">
      <Row className="mb-4">
        <Col>
          <h2>Manage User Roles</h2>
        </Col>
        <Col xs="auto">
          <Button variant="success" onClick={() => setShowModal(true)}>
            <FaPlus className="me-2" /> Create New Role
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Role Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.length > 0 ? (
                roles.map((role, index) => (
                  <tr key={role.id}>
                    <td>{index + 1}</td>
                    <td>{role.name}</td>
                    <td>
                      {isDefaultRole(role) ? (
                        <Badge bg="primary">System Role</Badge>
                      ) : (
                        <Badge bg="info">Custom Role</Badge>
                      )}
                    </td>
                    <td>
                      {!isDefaultRole(role) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteRole(role.id, role.name)}
                        >
                          <FaTrash /> Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No roles found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add Role Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title>Create New Role</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddRole}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Role Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter role name (e.g., Nurse, Pharmacist)"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Role
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ManageRoles;
